/**
 * Pure helpers for the participating-parties feature. Everything here is a function of its
 * inputs so the transfer list, the diff strips and the save planner can be unit-tested
 * without a DOM or a backend.
 */
import type {
	BallotSource,
	IBallotConflict,
	IBallotParties,
	ICycleParty,
	ICyclePartyInput,
	IMatrixCycle,
	IPartyMatrix,
} from '../../../interface/state/IBallotState';

// ─── Transfer list ──────────────────────────────────────────────────────────

/** The draft is the ordered list of party ids on the ballot; position = ballot order. */
export type BallotDraft = number[];

export type BallotDraftAction =
	| { type: 'add'; ids: number[] }
	| { type: 'remove'; ids: number[] }
	| { type: 'move'; id: number; direction: 'up' | 'down' }
	| { type: 'replace'; ids: number[] };

/** Appends in the order given, skipping ids already on the ballot. */
export const ballotDraftReducer = (draft: BallotDraft, action: BallotDraftAction): BallotDraft => {
	switch (action.type) {
		case 'add': {
			const present = new Set(draft);
			const additions = action.ids.filter((id, i) => !present.has(id) && action.ids.indexOf(id) === i);
			return additions.length ? [...draft, ...additions] : draft;
		}
		case 'remove': {
			const drop = new Set(action.ids);
			const next = draft.filter((id) => !drop.has(id));
			return next.length === draft.length ? draft : next;
		}
		case 'move': {
			const from = draft.indexOf(action.id);
			const to = action.direction === 'up' ? from - 1 : from + 1;
			if (from < 0 || to < 0 || to >= draft.length) return draft;
			const next = [...draft];
			[next[from], next[to]] = [next[to], next[from]];
			return next;
		}
		case 'replace':
			return Array.from(new Set(action.ids));
		default:
			return draft;
	}
};

/** Saved roster → draft, honouring ballot order (unordered rows keep their server order, last). */
export const draftFromRoster = (roster: Pick<ICycleParty, 'partyId' | 'ballotOrder'>[]): BallotDraft =>
	roster
		.map((p, index) => ({ id: p.partyId, order: p.ballotOrder ?? Number.MAX_SAFE_INTEGER, index }))
		.sort((a, b) => a.order - b.order || a.index - b.index)
		.map((p) => p.id);

/** Draft → PUT body. Position on screen is the ballot order, 1-based. */
export const draftToInput = (draft: BallotDraft): ICyclePartyInput[] =>
	draft.map((partyId, index) => ({ partyId, ballotOrder: index + 1 }));

export interface IDraftDiff {
	added: number[];
	removed: number[];
	/** Same membership, different order. */
	reordered: boolean;
	changed: boolean;
}

export const diffDraft = (saved: BallotDraft, draft: BallotDraft): IDraftDiff => {
	const savedSet = new Set(saved);
	const draftSet = new Set(draft);
	const added = draft.filter((id) => !savedSet.has(id));
	const removed = saved.filter((id) => !draftSet.has(id));
	const kept = draft.filter((id) => savedSet.has(id));
	const savedKept = saved.filter((id) => draftSet.has(id));
	const reordered = kept.some((id, i) => savedKept[i] !== id);
	return { added, removed, reordered, changed: added.length > 0 || removed.length > 0 || reordered };
};

/** Case- and whitespace-insensitive match on acronym or full name. */
export const matchesPartySearch = (party: { shortName?: string; longName?: string }, term: string): boolean => {
	const needle = term.trim().toLowerCase();
	if (!needle) return true;
	return `${party.shortName ?? ''} ${party.longName ?? ''}`.toLowerCase().includes(needle);
};

// ─── Changes vs the previous attribute ──────────────────────────────────────

export interface IRosterChange {
	/** On this ballot, not on the previous one. */
	joined: number[];
	/** On the previous ballot, not on this one. */
	left: number[];
}

export const diffRosters = (previous: number[], current: number[]): IRosterChange => {
	const prev = new Set(previous);
	const cur = new Set(current);
	return {
		joined: current.filter((id) => !prev.has(id)),
		left: previous.filter((id) => !cur.has(id)),
	};
};

const timeOf = (cycle: Pick<IMatrixCycle, 'startDate' | 'year'>): number => {
	const t = Date.parse(cycle.startDate);
	if (Number.isFinite(t)) return t;
	const y = Number.parseInt(cycle.year, 10);
	return Number.isFinite(y) ? Date.UTC(y, 0, 1) : 0;
};

/** Chronological order, oldest first; ties broken by id so the order is total. */
export const sortCyclesChronologically = <T extends Pick<IMatrixCycle, 'id' | 'startDate' | 'year'>>(cycles: T[]): T[] =>
	[...cycles].sort((a, b) => timeOf(a) - timeOf(b) || a.id - b.id);

/**
 * The attribute this one is compared against: the chronologically previous cycle of the SAME
 * election type. Comparing a 2023 general against a 2022 off-cycle governorship would report
 * every party that only contests one of them as having joined or left.
 */
export const previousCycleOf = (cycles: IMatrixCycle[], cycleId: number): IMatrixCycle | undefined => {
	const self = cycles.find((c) => c.id === cycleId);
	if (!self) return undefined;
	const sameType = sortCyclesChronologically(cycles.filter((c) => c.electionTypeId === self.electionTypeId));
	const index = sameType.findIndex((c) => c.id === cycleId);
	return index > 0 ? sameType[index - 1] : undefined;
};

export const partyIdsForCycle = (matrix: Pick<IPartyMatrix, 'cells'> | undefined, cycleId: number): number[] =>
	(matrix?.cells ?? []).filter(([c]) => c === cycleId).map(([, p]) => p);

// ─── Membership writes that must not disturb other parties ──────────────────

/**
 * Applies adds/removes to a FRESH copy of a cycle's roster and returns the whole-set body.
 *
 * The endpoint replaces the set, so a single-party change must carry every other party along
 * with its existing ballot order. New parties go to the end, numbered after the current maximum.
 */
export const applyMembershipChanges = (
	roster: Pick<ICycleParty, 'partyId' | 'ballotOrder'>[],
	changes: { add?: number[]; remove?: number[] }
): ICyclePartyInput[] => {
	const drop = new Set(changes.remove ?? []);
	const kept = roster.filter((p) => !drop.has(p.partyId));
	const present = new Set(kept.map((p) => p.partyId));
	const next = kept.reduce((max, p) => Math.max(max, p.ballotOrder ?? 0), 0);
	const additions = (changes.add ?? []).filter((id, i, all) => !present.has(id) && all.indexOf(id) === i && !drop.has(id));
	return [
		...kept.map((p) => (p.ballotOrder == null ? { partyId: p.partyId } : { partyId: p.partyId, ballotOrder: p.ballotOrder })),
		...additions.map((partyId, i) => ({ partyId, ballotOrder: next + i + 1 })),
	];
};

// ─── Errors ─────────────────────────────────────────────────────────────────

export type BallotError =
	| { kind: 'conflict'; conflicts: IBallotConflict[]; message: string }
	/** 409 with no body: another write landed first. Safe to resend; nothing to confirm. */
	| { kind: 'concurrent'; message: string }
	| { kind: 'unknown-parties'; partyIds: number[]; message: string }
	/** 400 from the per-election override: ids outside the attribute's roster. */
	| { kind: 'not-in-attribute'; partyIds: number[]; message: string }
	| { kind: 'not-found'; message: string }
	| { kind: 'other'; message: string };

interface IErrorLike {
	response?: { status?: number; data?: { message?: string; Message?: string; data?: unknown; Data?: unknown } };
	message?: string;
}

/** Reads an axios-shaped error defensively (either casing; absent fields are not assumed). */
export const parseBallotError = (err: unknown, fallback = 'Something went wrong. Try again.'): BallotError => {
	const e = (err ?? {}) as IErrorLike;
	const status = e.response?.status;
	const body = e.response?.data;
	const message = body?.message || body?.Message || fallback;
	const data = (body?.data ?? body?.Data) as
		| { conflicts?: IBallotConflict[]; unknownPartyIds?: number[]; notInAttributePartyIds?: number[] }
		| undefined;
	if (status === 409 && Array.isArray(data?.conflicts) && data.conflicts.length)
		return { kind: 'conflict', conflicts: data.conflicts, message };
	if (status === 409) return { kind: 'concurrent', message };
	if (status === 400 && Array.isArray(data?.notInAttributePartyIds))
		return { kind: 'not-in-attribute', partyIds: data.notInAttributePartyIds, message };
	if (status === 400 && Array.isArray(data?.unknownPartyIds))
		return { kind: 'unknown-parties', partyIds: data.unknownPartyIds, message };
	if (status === 404) return { kind: 'not-found', message };
	return { kind: 'other', message };
};

/**
 * Plain-text body for the force confirmation; the dialog renders a single paragraph.
 *
 * Built from `conflicts[]` alone, never from the save's `removed`: an election override that
 * predates the roster can name a party the attribute never listed, so it conflicts without
 * being "removed" by this save.
 */
export const conflictMessage = (conflicts: IBallotConflict[], cycleName: string): string => {
	const lines = conflicts.map((c) => {
		const names = c.elections.map((e) => e.name).join(', ');
		return `${c.shortName} is on the custom list of ${c.elections.length === 1 ? '1 election' : `${c.elections.length} elections`}: ${names}.`;
	});
	const them = conflicts.length === 1 ? 'it' : 'them';
	return `${lines.join(' ')} The ${cycleName} ballot will not include ${them}, so saving also removes ${them} from those elections.`;
};

// ─── Copy ───────────────────────────────────────────────────────────────────

export const pluralParties = (n: number): string => (n === 1 ? '1 party' : `${n} parties`);

/** The note every Phase 1 reader shows when no ballot narrows the party list. */
export const BALLOT_FALLBACK_NOTE = 'Showing all active parties — no ballot set for this election.';

/** `legacy` = the resolver is unavailable (older backend), which reads the same to the user. */
export const ballotFallbackNote = (source: BallotSource | 'legacy' | undefined): string | null =>
	source === 'fallback' || source === 'legacy' ? BALLOT_FALLBACK_NOTE : null;

/** "Uses attribute list" / "Custom: 12 of 18" / "No ballot set". */
export const electionSourceLabel = (source: BallotSource | undefined, partyCount: number, rosterCount: number): string => {
	if (source === 'election') return `Custom: ${partyCount} of ${rosterCount}`;
	if (source === 'attribute') return 'Uses attribute list';
	if (source === 'fallback') return 'No ballot set';
	return 'Checking…';
};

// ─── Phase 1 readers ────────────────────────────────────────────────────────

/**
 * Narrows a full party list to an election's ballot, in ballot order, keeping each row's
 * original object so downstream shapes are unchanged.
 *
 * No answer, or a `fallback` answer, returns the list untouched — the pre-feature behaviour.
 * So does a ballot naming no party in `all`: an empty result form is worse than an unscoped one.
 */
export const scopePartiesToBallot = <T extends { id: number }>(all: T[], ballot: IBallotParties | null | undefined): T[] => {
	if (!ballot || ballot.source === 'fallback' || !ballot.parties.length) return all;
	const byId = new Map(all.map((p) => [p.id, p]));
	const scoped = ballot.parties
		.map((b, i) => ({ party: byId.get(b.id), order: b.ballotOrder ?? Number.MAX_SAFE_INTEGER, i }))
		.filter((x): x is { party: T; order: number; i: number } => !!x.party)
		.sort((a, b) => a.order - b.order || a.i - b.i)
		.map((x) => x.party);
	return scoped.length ? scoped : all;
};

// ─── Concurrency ────────────────────────────────────────────────────────────

/** A tiny semaphore: at most `limit` of the wrapped tasks run at once. */
export const createLimiter = (limit: number) => {
	let active = 0;
	const queue: (() => void)[] = [];
	const next = () => {
		if (active >= limit) return;
		const start = queue.shift();
		if (start) start();
	};
	return <T>(task: () => Promise<T>): Promise<T> =>
		new Promise<T>((resolve, reject) => {
			queue.push(() => {
				active += 1;
				task()
					.then(resolve, reject)
					.finally(() => {
						active -= 1;
						next();
					});
			});
			next();
		});
};
